type BlobLike = Pick<Blob, 'text' | 'arrayBuffer'>;

function isBlobLike(data: unknown): data is BlobLike {
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const d = data as { text?: unknown; arrayBuffer?: unknown };
    return typeof d.text === 'function' || typeof d.arrayBuffer === 'function';
}

/** jsdom Blob has no `.text()` / `.arrayBuffer()`; browsers and Node do. */
async function readBlobAsUtf8(blob: Blob): Promise<string> {
    if (typeof blob.text === 'function') {
        return blob.text();
    }
    if (typeof blob.arrayBuffer === 'function') {
        const ab = await blob.arrayBuffer();
        return new TextDecoder().decode(ab);
    }
    if (typeof FileReader !== 'undefined') {
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(String(fr.result ?? ''));
            fr.onerror = () => reject(fr.error ?? new Error('FileReader failed'));
            fr.readAsText(blob);
        });
    }
    throw new Error('unreadable-blob');
}

async function readBlobLikeAsUtf8(data: BlobLike): Promise<string> {
    if (typeof data.text === 'function') {
        return data.text();
    }
    const ab = await data.arrayBuffer();
    return new TextDecoder().decode(ab);
}

/**
 * Human-readable message from an Axios error response.
 * With responseType: 'blob', failed responses still carry JSON in a Blob body — parse it for `detail`.
 */
export async function messageFromAxiosErrorResponse(
    response: { data?: unknown } | undefined,
    fallback: string,
): Promise<string> {
    const data = response?.data;
    if (data == null) {
        return fallback;
    }
    if (typeof data === 'string') {
        return parseJsonDetailString(data, fallback);
    }
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
        return parseJsonDetailString(data.toString('utf8'), fallback);
    }
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await readBlobAsUtf8(data);
            return parseJsonDetailString(text, fallback);
        } catch {
            return fallback;
        }
    }
    if (isBlobLike(data)) {
        try {
            const text = await readBlobLikeAsUtf8(data);
            return parseJsonDetailString(text, fallback);
        } catch {
            return fallback;
        }
    }
    if (typeof data === 'object') {
        return detailFromBodyObject(data, fallback);
    }
    return fallback;
}

function parseJsonDetailString(text: string, fallback: string): string {
    const trimmed = text.trim();
    if (!trimmed) return fallback;
    try {
        const j = JSON.parse(trimmed) as unknown;
        if (typeof j === 'object' && j !== null) {
            return detailFromBodyObject(j, fallback);
        }
    } catch {
        return trimmed;
    }
    return fallback;
}

function detailFromBodyObject(body: object, fallback: string): string {
    if (!('detail' in body)) {
        return fallback;
    }
    const d = (body as { detail: unknown }).detail;
    if (typeof d === 'string') {
        return d;
    }
    if (Array.isArray(d)) {
        const parts = d.map((item) => {
            if (typeof item === 'object' && item !== null && 'msg' in item) {
                return String((item as { msg: unknown }).msg);
            }
            return String(item);
        });
        return parts.filter(Boolean).join('; ') || fallback;
    }
    return fallback;
}
